/** Mirrors server/checkout-quote.js so barista Order details can show tax/fees
 *  from stored columns or reconstruct them for older tickets. */

import { deliveryFeeJod } from './deliveryFee';

const BASE_SERVICE_FEE_PERCENT = Number(process.env.REACT_APP_SERVICE_FEE_PERCENT || 5);
const SUBSCRIBER_SERVICE_FEE_PERCENT = Number(process.env.REACT_APP_SUBSCRIBER_SERVICE_FEE_PERCENT || 4);
const SERVICE_FEE_MIN_JOD = Number(process.env.REACT_APP_SERVICE_FEE_MIN_JOD || 0.2);
const SERVICE_FEE_MAX_JOD = Number(process.env.REACT_APP_SERVICE_FEE_MAX_JOD || 1.0);
const SMALL_ORDER_THRESHOLD_JOD = Number(process.env.REACT_APP_SMALL_ORDER_THRESHOLD_JOD || 3.0);
const SMALL_ORDER_FEE_JOD = Number(process.env.REACT_APP_SMALL_ORDER_FEE_JOD || 0.3);
const JORDAN_TAX_PERCENT = Number(process.env.REACT_APP_JORDAN_TAX_PERCENT || 16);

export const ORDER_TAX_PERCENT = JORDAN_TAX_PERCENT;

function round2(n: number): number {
  return Math.round(Number(n || 0) * 100) / 100;
}

export interface OrderChargeFields {
  total_amount?: number;
  merchandise_subtotal_jod?: number | null;
  promotion_discount?: number | null;
  student_discount?: number | null;
  points_used?: number | null;
  points_discount_jod?: number | null;
  wallet_used?: number | null;
  fulfillment_type?: string | null;
  delivery_distance_km?: number | null;
  jordan_tax_jod?: number | null;
  service_fee_jod?: number | null;
  small_order_fee_jod?: number | null;
  delivery_fee_jod?: number | null;
  fees_jod?: number | null;
  dine_in_discount_jod?: number | null;
  dine_in_discount_percent?: number | null;
}

export interface OrderFinancialBreakdown {
  subtotal: number;
  promotionDiscount: number;
  tax: number;
  fees: number;
  serviceFee: number;
  serviceFeePercent: number;
  smallOrderFee: number;
  deliveryFee: number;
  dineInDiscount: number;
  pointsUsed: number;
  pointsDiscount: number;
  walletUsed: number;
  configured: boolean;
  netPayout: number;
}

function calculateJordanTax(amountBeforeTax: number): number {
  const safeBase = Number(amountBeforeTax) || 0;
  if (safeBase <= 0 || JORDAN_TAX_PERCENT <= 0) return 0;
  return round2(safeBase * (JORDAN_TAX_PERCENT / 100));
}

function calculateServiceFee(amountBase: number, isSubscriber = false): number {
  const safeBase = Math.max(0, Number(amountBase) || 0);
  if (safeBase <= 0) return 0;
  const percent = isSubscriber ? SUBSCRIBER_SERVICE_FEE_PERCENT : BASE_SERVICE_FEE_PERCENT;
  const rawFee = safeBase * (percent / 100);
  return round2(Math.max(SERVICE_FEE_MIN_JOD, Math.min(SERVICE_FEE_MAX_JOD, rawFee)));
}

function serviceFeePercentFor(amountBase: number, serviceFee: number): number {
  const stored = round2(serviceFee);
  const subscriberFee = calculateServiceFee(amountBase, true);
  const regularFee = calculateServiceFee(amountBase, false);
  if (stored === subscriberFee) return SUBSCRIBER_SERVICE_FEE_PERCENT;
  if (stored === regularFee) return BASE_SERVICE_FEE_PERCENT;
  return BASE_SERVICE_FEE_PERCENT;
}

export function merchandiseFromOrderItems(
  items?: Array<{ price?: number | null; quantity?: number | null }> | null
): number {
  return round2(
    (items || []).reduce((sum, item) => {
      const qty = Math.max(0, Number(item?.quantity) || 0);
      const price = Math.max(0, Number(item?.price) || 0);
      return sum + price * qty;
    }, 0)
  );
}

export function getOrderFinancialBreakdown(order: OrderChargeFields): OrderFinancialBreakdown {
  const merch =
    order.merchandise_subtotal_jod != null && Number.isFinite(Number(order.merchandise_subtotal_jod))
      ? round2(Math.max(0, Number(order.merchandise_subtotal_jod)))
      : null;
  const promotionDiscount = round2(Math.max(0, Number(order.promotion_discount || 0)));
  const pointsDiscount = round2(Math.max(0, Number(order.points_discount_jod || 0)));
  const walletUsed = round2(Math.max(0, Number(order.wallet_used || 0)));
  const dineInDiscount = round2(Math.max(0, Number(order.dine_in_discount_jod || 0)));
  const hasStoredTax = order.jordan_tax_jod != null && order.service_fee_jod != null;

  if (hasStoredTax) {
    const tax = round2(Number(order.jordan_tax_jod) || 0);
    const serviceFee = round2(Number(order.service_fee_jod) || 0);
    const smallOrderFee = round2(Number(order.small_order_fee_jod) || 0);
    const deliveryFee = round2(Number(order.delivery_fee_jod) || 0);
    const fees =
      order.fees_jod != null
        ? round2(Number(order.fees_jod))
        : round2(serviceFee + smallOrderFee + deliveryFee);
    const subtotal = merch ?? round2(Number(order.total_amount || 0));
    const afterPromo = Math.max(0, round2(subtotal - promotionDiscount));
    const afterPoints = Math.max(0, round2(afterPromo - pointsDiscount));
    return {
      subtotal,
      promotionDiscount,
      tax,
      fees,
      serviceFee,
      serviceFeePercent: serviceFeePercentFor(afterPoints, serviceFee),
      smallOrderFee,
      deliveryFee,
      dineInDiscount,
      pointsUsed: Number(order.points_used || 0),
      pointsDiscount,
      walletUsed,
      configured: true,
      netPayout: round2(Number(order.total_amount || 0)),
    };
  }

  if (merch == null) {
    return {
      subtotal: round2(Number(order.total_amount || 0)),
      promotionDiscount,
      tax: 0,
      fees: 0,
      serviceFee: 0,
      serviceFeePercent: BASE_SERVICE_FEE_PERCENT,
      smallOrderFee: 0,
      deliveryFee: 0,
      dineInDiscount,
      pointsUsed: Number(order.points_used || 0),
      pointsDiscount,
      walletUsed,
      configured: false,
      netPayout: round2(Number(order.total_amount || 0)),
    };
  }

  const afterPromo = Math.max(0, round2(merch - promotionDiscount));
  const appliedPointsDiscount = Math.min(afterPromo, pointsDiscount);
  const afterPoints = Math.max(0, round2(afterPromo - appliedPointsDiscount));
  const appliedWallet = round2(Math.min(walletUsed, afterPoints));
  const afterWallet = Math.max(0, round2(afterPoints - appliedWallet));
  const serviceFee = calculateServiceFee(afterPoints);
  const smallOrderFee =
    afterPoints > 0 && afterPoints < SMALL_ORDER_THRESHOLD_JOD ? SMALL_ORDER_FEE_JOD : 0;
  const deliveryFee =
    String(order.fulfillment_type || 'pickup').toLowerCase().trim() === 'delivery'
      ? deliveryFeeJod(order.delivery_distance_km)
      : 0;
  const amountBeforeTax = round2(afterWallet + deliveryFee + serviceFee + smallOrderFee);
  const tax = calculateJordanTax(amountBeforeTax);

  return {
    subtotal: merch,
    promotionDiscount,
    tax,
    fees: round2(serviceFee + smallOrderFee + deliveryFee),
    serviceFee,
    serviceFeePercent: serviceFeePercentFor(afterPoints, serviceFee),
    smallOrderFee,
    deliveryFee,
    dineInDiscount,
    pointsUsed: Number(order.points_used || 0),
    pointsDiscount: appliedPointsDiscount,
    walletUsed: appliedWallet,
    configured: true,
    netPayout: round2(Number(order.total_amount || 0)),
  };
}
